import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { DataContext, GroupContext } from "../../lib/contexts";
import { useNavigate } from "react-router-dom";
import { DishPopup } from "../dishes/dishPopup";
import { DishImage } from "../dishes/dishImage";
import {
  callEndpoint,
  doFormalize,
  getStringFromYearWeekDay,
} from "../../lib/functions";
import { ActionName, dishTypeToColor, LF_LAST_FOLDER } from "../../lib/lib";
import { MInput } from "../elements/mInputs";
import { FcOpenedFolder } from "react-icons/fc";
import { MModal } from "../elements/mmodal";
import * as localForage from "localforage";
import { FaChevronLeft } from "react-icons/fa";
import {
  AddDishesToFolderEndpoint,
  CreateFolderEndpoint,
  DeleteFolderEndpoint,
  Dish,
  Folder,
  RemoveDishesFromFolderEndpoint,
  UpdateFolderEndpoint,
  UpdateWeekPlanEndpoint,
  WeekPlan,
} from "@ckal-software/ckal-lib/dist/apps/madplanen";
import { useNotifcation } from "../../lib/hooks/useNotification";
import { useScrollToTop } from "../../lib/hooks/useScrollToTop";
import { MIconButton } from "../elements/miconButton";
import { MButton } from "../elements/mbutton";
import { ConfirmModal } from "../elements/confirmModal";
import css from "./catalogView.module.css";

export function CatalogueView() {
  const [searchValue, setSearchValue] = useState("");
  const searchResultsRef = useScrollToTop(searchValue);
  const [selectedDish, setSelectedDish] = useState<Dish>();
  const [editingFolder, setEditingFolder] = useState<Folder>();
  const [folderName, setFolderName] = useState("");
  const [isUpdatingFolder, setIsUpdatingFolder] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [randomDish, setRandomDish] = useState<Dish>();
  const [pendingDishRemoval, setPendingDishRemoval] = useState<{
    dish: Dish;
    folder: Folder;
  }>();
  const [isRemovingDish, setIsRemovingDish] = useState(false);

  const noScroll = useRef(false);
  const notify = useNotifcation();

  const { group } = useContext(GroupContext);
  const {
    dishes,
    refreshWeekPlan,
    weekPlan,
    folders,
    isFetchingFolders,
    isFetchingDishes,
    refreshFolders,
    setCurrentAction,
    currentAction,
    setWeekPlan,
    setFolders,
    selectedFolder,
    setSelectedFolder,
  } = useContext(DataContext);

  const navigate = useNavigate();

  useEffect(() => {
    setSelectedDish(
      dishes.find(
        (dish) =>
          dish._id ===
            new URLSearchParams(window.location.search).get("selected-dish") ||
          "",
      ),
    );
  }, [dishes]);

  useEffect(() => {
    if (!currentAction) {
      setSearchValue("");
    }

    if (currentAction?.action === "SELECT_FOLDER_FOR_DISH") {
      setSelectedFolder(undefined);
    }
  }, [currentAction, setSelectedFolder]);

  useEffect(() => {
    setFolderName(selectedFolder?.folderName || "");
    if (noScroll.current) {
      noScroll.current = false;
      return;
    }
    document.getElementById("folder-dish-container")?.scrollTo(0, 0);
  }, [selectedFolder]);

  const filteredDishes = useMemo(() => {
    const matchingDishes = dishes
      .filter((dish) =>
        doFormalize(dish.name).includes(doFormalize(searchValue)),
      )
      .filter((dish) => dish.groupShortId === group?.shortId)
      .filter(
        (dish) =>
          !selectedFolder ||
          selectedFolder.dishIds.includes(dish._id) ||
          selectedFolder.folderName === "Alle",
      )
      .filter(
        (dish) =>
          currentAction?.action !== "SELECT_DISH_FOR_FOLDER" ||
          !currentAction.data.dishIds.includes(dish._id),
      );

    return matchingDishes.sort((leftDish, rightDish) =>
      leftDish.name.localeCompare(rightDish.name, "da", {
        sensitivity: "base",
      }),
    );
  }, [dishes, searchValue, group?.shortId, selectedFolder, currentAction]);

  const groupDishCount = useMemo(
    () => dishes.filter((dish) => dish.groupShortId === group?.shortId).length,
    [dishes, group?.shortId],
  );

  function startAddingDishesToFolder(folder: Folder) {
    setCurrentAction({
      action: ActionName.SelectDishForFolder,
      data: folder,
    });
    setSelectedFolder(folders.find((f) => f.folderName === "Alle"));
  }

  async function removeDishFromFolder() {
    if (!pendingDishRemoval) {
      return;
    }

    const { dish, folder } = pendingDishRemoval;
    const [err] = await callEndpoint(
      RemoveDishesFromFolderEndpoint,
      {
        folderId: folder._id,
        dishIds: [dish._id],
      },
      { setWorking: setIsRemovingDish },
    );

    if (err) {
      notify("Sletning af ret fra mappe: Der skete en fejl");
      return;
    }

    noScroll.current = true;
    const updatedFolder = {
      ...folder,
      dishIds: folder.dishIds.filter((dishId) => dishId !== dish._id),
    };
    setSelectedFolder(updatedFolder);
    setFolders(
      folders.map((currentFolder) =>
        currentFolder._id === folder._id ? updatedFolder : currentFolder,
      ),
    );
    setPendingDishRemoval(undefined);
    refreshFolders();
  }

  function updateRandomDish() {
    const groupDishes = dishes.filter(
      (dish) => dish.groupShortId === group?.shortId,
    );
    setRandomDish(groupDishes[Math.floor(Math.random() * groupDishes.length)]);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const container = document.getElementById("folder-dish-container");
        container?.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      });
    });
  }

  async function handleDishClick(dish: Dish) {
    if (currentAction?.action === "SELECT_DISH_FOR_DAY") {
      setCurrentAction(undefined);
      navigate(`/week-plan?week=${currentAction.data.week}`);

      const ywdString = getStringFromYearWeekDay(currentAction.data);
      const newDishes = (weekPlan?.weekPlans[ywdString] || []).concat([
        dish._id,
      ]);

      const newWeekPlan: WeekPlan = JSON.parse(JSON.stringify(weekPlan));
      newWeekPlan.weekPlans[ywdString] = newDishes;
      setWeekPlan(newWeekPlan);

      const [err] = await callEndpoint(UpdateWeekPlanEndpoint, {
        yearWeekDay: ywdString,
        dishIds: newDishes,
      });

      if (err) {
        notify("Opdatering af madplan: Der skete en fejl");
        return;
      }
      refreshWeekPlan();
    } else if (currentAction?.action === "SELECT_DISH_FOR_FOLDER") {
      setSearchValue("");

      setFolders(
        folders.map((f) =>
          f._id === currentAction.data._id
            ? { ...f, dishIds: f.dishIds.concat(dish._id) }
            : f,
        ),
      );
      setCurrentAction({
        ...currentAction,
        data: {
          ...currentAction.data,
          dishIds: currentAction.data.dishIds.concat(dish._id),
        },
      });

      const [err] = await callEndpoint(AddDishesToFolderEndpoint, {
        folderId: currentAction.data._id,
        dishIds: [dish._id],
      });

      if (err) {
        notify("Opdatering af mappe: Der skete en fejl");
        return;
      }
      localForage.setItem(LF_LAST_FOLDER, currentAction.data._id);
    } else {
      setSelectedDish(dish);
    }
  }

  return (
    <div className={css.wrapper}>
      <>
        {/* Top bar */}
        <div className={css.topBar}>
          <MInput
            prefix={<SearchOutlined className={css.searchIcon} />}
            size="large"
            style={{ height: 44 }}
            placeholder={selectedFolder ? "Søg opskrifter..." : "Søg mapper..."}
            allowClear
            className="new-item-input"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          {!currentAction && (
            <>
              {isFetchingFolders || isFetchingDishes ? (
                <LoadingOutlined className={css.loadingIcon} />
              ) : (
                <MIconButton
                  icon={<PlusOutlined />}
                  type="primary"
                  onClick={() => navigate("/upsert-dish")}
                />
              )}
            </>
          )}
        </div>

        {/* Content */}
        <div
          className={css.content}
          id="folder-dish-container"
          ref={searchResultsRef}
        >
          {selectedFolder ? (
            <>
              {/* Breadcrumb */}
              <div className={css.breadcrumb}>
                <span
                  className={css.breadcrumbBack}
                  onClick={() => {
                    setSelectedFolder(undefined);
                    localForage.setItem(LF_LAST_FOLDER, "");
                  }}
                >
                  <FaChevronLeft />
                </span>
                <FcOpenedFolder
                  className={css.breadcrumbIcon}
                  onClick={() => {
                    setSelectedFolder(undefined);
                    localForage.setItem(LF_LAST_FOLDER, "");
                  }}
                />
                <span
                  className={css.breadcrumbName}
                  onClick={() => {
                    setSelectedFolder(undefined);
                    localForage.setItem(LF_LAST_FOLDER, "");
                  }}
                >
                  {selectedFolder.folderName}
                </span>
                {selectedFolder.folderName !== "Alle" && !currentAction && (
                  <MIconButton
                    title="Rediger mappe"
                    icon={<EditOutlined />}
                    background="white"
                    onClick={() => {
                      setIsUpdatingFolder(false);
                      setEditingFolder(selectedFolder);
                    }}
                  />
                )}
              </div>

              {/* Dishes in folder */}
              {filteredDishes.length === 0 ? (
                searchValue ? (
                  <div className={css.emptyState}>
                    <span className={css.emptyText}>
                      {`Ingen retter matcher "${searchValue}"`}
                    </span>
                  </div>
                ) : null
              ) : (
                <div className={css.dishList}>
                  {filteredDishes.map((dish) => (
                    <div
                      key={dish._id}
                      className={css.dishCard}
                      onClick={() => handleDishClick(dish)}
                    >
                      <DishImage
                        className={css.dishCardImage}
                        src={dish.imageUrl}
                        alt=""
                      />
                      <div className={css.dishCardInfo}>
                        <div className={css.dishCardName}>{dish.name}</div>
                        <div className={css.dishCardMeta}>
                          <span
                            className={css.dishCardLabel}
                            style={{
                              background: dishTypeToColor[dish.type] + "20",
                              color: dishTypeToColor[dish.type],
                            }}
                          >
                            {dish.type}
                          </span>
                          <span className={css.dishCardTime}>
                            {dish.time} min
                          </span>
                        </div>
                      </div>
                      {selectedFolder.folderName !== "Alle" &&
                        !currentAction && (
                          <div
                            className={css.dishCardDelete}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MIconButton
                              icon={<DeleteOutlined />}
                              color="#ef4444"
                              background="#fef2f2"
                              style={{ width: 36, height: 36, fontSize: 17 }}
                              onClick={() =>
                                setPendingDishRemoval({
                                  dish,
                                  folder: selectedFolder,
                                })
                              }
                            />
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add dishes to folder button */}
              {selectedFolder.folderName !== "Alle" && !currentAction && (
                <button
                  type="button"
                  className={css.dishCardDashed}
                  onClick={() => startAddingDishesToFolder(selectedFolder)}
                >
                  <PlusOutlined style={{ fontSize: 22 }} />
                  Tilføj retter
                </button>
              )}
            </>
          ) : (
            <>
              {/* Folder grid */}
              <div className={css.folderGrid}>
                {folders
                  .filter(
                    (f) =>
                      currentAction?.action !== "SELECT_FOLDER_FOR_DISH" ||
                      (f.folderName !== "Alle" &&
                        !f.dishIds.includes(currentAction.data._id)),
                  )
                  .filter(
                    (f) =>
                      !searchValue ||
                      doFormalize(f.folderName).includes(
                        doFormalize(searchValue),
                      ),
                  )
                  .map((folder) => (
                    <div
                      key={folder._id}
                      className={css.folderCard}
                      onClick={async () => {
                        if (
                          currentAction?.action === "SELECT_FOLDER_FOR_DISH"
                        ) {
                          setFolders(
                            folders.map((f) =>
                              f._id === folder._id
                                ? {
                                    ...f,
                                    dishIds: f.dishIds.concat(
                                      currentAction.data._id,
                                    ),
                                  }
                                : f,
                            ),
                          );
                          setSelectedDish(currentAction.data);
                          setSelectedFolder(folder);
                          await localForage.setItem(LF_LAST_FOLDER, folder._id);
                          setCurrentAction(undefined);

                          const [err] = await callEndpoint(
                            AddDishesToFolderEndpoint,
                            {
                              folderId: folder._id,
                              dishIds: [currentAction.data._id],
                            },
                          );

                          if (err) {
                            notify(
                              "Tilføjelse af ret til mappe: Der skete en fejl",
                            );
                            return;
                          }
                        } else {
                          setSelectedFolder(folder);
                          setSearchValue("");
                          localForage.setItem(LF_LAST_FOLDER, folder._id);
                        }
                      }}
                    >
                      <FcOpenedFolder className={css.folderCardIcon} />
                      <span className={css.folderCardName}>
                        {folder.folderName}
                      </span>
                      <span className={css.folderCardCount}>
                        {folder.folderName === "Alle"
                          ? groupDishCount
                          : folder.dishIds.length}{" "}
                        retter
                      </span>
                    </div>
                  ))}
                {!currentAction && (
                  <div
                    className={css.folderCardDashed}
                    onClick={() => setCreatingFolder(true)}
                  >
                    <PlusOutlined style={{ fontSize: 22 }} />
                    Ny mappe
                  </div>
                )}
              </div>

              {/* Random dish */}
              {currentAction?.action !== ActionName.SelectFolderForDish && (
                <div className={css.randomSection}>
                  <div className={css.randomTitle}>Prøv lykken</div>
                  <div className={css.actionRow}>
                    <MButton type="white" onClick={() => updateRandomDish()}>
                      🎲 Tilfældig ret
                    </MButton>
                  </div>
                  {randomDish && (
                    <div
                      className={css.dishCard}
                      onClick={() => handleDishClick(randomDish)}
                    >
                      <DishImage
                        className={css.dishCardImage}
                        src={randomDish.imageUrl}
                        alt=""
                      />
                      <div className={css.dishCardInfo}>
                        <div className={css.dishCardName}>
                          {randomDish.name}
                        </div>
                        <div className={css.dishCardMeta}>
                          <span
                            className={css.dishCardLabel}
                            style={{
                              background:
                                dishTypeToColor[randomDish.type] + "20",
                              color: dishTypeToColor[randomDish.type],
                            }}
                          >
                            {randomDish.type}
                          </span>
                          <span className={css.dishCardTime}>
                            {randomDish.time} min
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </>

      {/* Popups */}
      {selectedDish && (
        <DishPopup
          dish={selectedDish}
          visible={!!selectedDish}
          onClose={() => setSelectedDish(undefined)}
          onAddToWeekPlan={() => {
            setCurrentAction({
              action: ActionName.SelectDayForDish,
              data: selectedDish,
            });
            navigate("/week-plan");
          }}
          folders={folders}
        />
      )}
      {pendingDishRemoval && (
        <ConfirmModal
          visible
          onCancel={() => setPendingDishRemoval(undefined)}
          title="Fjern ret fra mappe"
          message={
            <>
              Vil du fjerne <strong>{pendingDishRemoval.dish.name}</strong> fra
              mappen <strong>{pendingDishRemoval.folder.folderName}</strong>?
            </>
          }
          confirmButton={
            <MIconButton
              title="Fjern ret fra mappe"
              type="danger"
              icon={<DeleteOutlined />}
              loading={isRemovingDish}
              onClick={removeDishFromFolder}
            />
          }
        />
      )}
      <MModal
        visible={creatingFolder}
        onClose={() => setCreatingFolder(false)}
        title="Ny mappe"
        hideCloseIcon
        content={
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "24px",
              }}
            >
              <FcOpenedFolder style={{ fontSize: "120px" }} />
            </div>
            <MInput
              size="large"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Mappenavn..."
            />
          </div>
        }
        footer={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <MButton
              type="ghost"
              onClick={() => {
                setCreatingFolder(false);
                setNewFolderName("");
              }}
            >
              Annuller
            </MButton>
            <MButton
              type="primary"
              icon={<PlusOutlined />}
              loading={isCreatingFolder}
              disabled={!newFolderName.trim()}
              onClick={async () => {
                const [err] = await callEndpoint(
                  CreateFolderEndpoint,
                  { name: newFolderName },
                  { setWorking: setIsCreatingFolder },
                );

                if (err) {
                  notify("Oprettelse af ny mappe: Der skete en fejl");
                  return;
                }

                setCreatingFolder(false);
                refreshFolders();
                setNewFolderName("");
              }}
            >
              Opret
            </MButton>
          </div>
        }
      />
      {editingFolder && (
        <MModal
          visible={!!editingFolder && !showDeleteConfirm}
          onClose={() => {
            setEditingFolder(undefined);
            setIsUpdatingFolder(false);
          }}
          title="Rediger mappe"
          content={
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "24px",
                }}
              >
                <FcOpenedFolder style={{ fontSize: "120px" }} />
              </div>
              <MInput
                placeholder="Mappenavn..."
                value={folderName}
                size="large"
                onChange={(e) => setFolderName(e.target.value)}
              />
            </div>
          }
          footer={
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <MIconButton
                icon={<DeleteOutlined />}
                type="danger"
                onClick={() => setShowDeleteConfirm(true)}
              />
              <MButton
                type="primary"
                icon={<EditOutlined />}
                disabled={
                  !folderName.trim() ||
                  folderName.trim() === editingFolder.folderName
                }
                loading={isUpdatingFolder}
                onClick={async () => {
                  const [err] = await callEndpoint(
                    UpdateFolderEndpoint,
                    {
                      id: editingFolder._id,
                      update: { name: folderName },
                    },
                    { setWorking: setIsUpdatingFolder },
                  );

                  if (err) {
                    notify("Opdatering af mappe: Der skete en fejl");
                    return;
                  }

                  refreshFolders();
                  setSelectedFolder({
                    ...editingFolder,
                    folderName: folderName.trim(),
                  });
                  setEditingFolder(undefined);
                }}
              >
                Opdater
              </MButton>
            </div>
          }
        />
      )}
      {editingFolder && (
        <ConfirmModal
          visible={showDeleteConfirm}
          title="Slet mappe"
          message={
            <>
              Er du sikker på, at du vil slette mappen{" "}
              <strong>{editingFolder.folderName}</strong>?
            </>
          }
          onCancel={() => setShowDeleteConfirm(false)}
          confirmButton={
            <MButton
              type="danger"
              icon={<DeleteOutlined />}
              onClick={async () => {
                const [err] = await callEndpoint(DeleteFolderEndpoint, {
                  id: editingFolder._id,
                });

                if (err) {
                  notify("Sletning af mappe: Der skete en fejl");
                  setShowDeleteConfirm(false);
                  return;
                }

                refreshFolders();
                setShowDeleteConfirm(false);
                setEditingFolder(undefined);
                setSelectedFolder(undefined);
              }}
            >
              Slet
            </MButton>
          }
        />
      )}
    </div>
  );
}
